/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Object-aware permissions, backward compatible with every existing call site.
 *
 *   usePermission()          -> exactly today's behaviour (support-user flag)
 *   usePermission('leads')   -> that object's grant, once the tenant enforces
 *
 * The no-argument form returning today's answer is the point. It lets the
 * provider ship, and tenants switch to enforcement, without touching the 70
 * files that already call this. Those convert one word at a time, per file,
 * with no coordinated migration.
 *
 * The decision rules live in ./permissionRules — pure and separately tested.
 */

import { useAuth } from './Auth';
import { usePermissionsContext } from './PermissionsProvider';
import { computeCapability, computePermission } from './permissionRules';

export { isSupportUser } from './permissionRules';

export const usePermission = (objectApiName) => {
  const { user } = useAuth();
  const { snapshot, ready, mode, isSuper } = usePermissionsContext();
  return computePermission({ user, objectApiName, snapshot, ready, mode, isSuper });
};

/** Non-object powers: MANAGE_USERS, MANAGE_ROLES, MANAGE_TEAMS, EXPORT. */
export const useCapability = (capability) => {
  const { user } = useAuth();
  const { capabilities, ready, mode, isSuper } = usePermissionsContext();
  return computeCapability({ user, capability, capabilities, ready, mode, isSuper });
};

export default usePermission;
